/* eslint-disable react/prop-types */
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { logEvent } from '../analyticsService'
import { ApplicationContext } from '../App'
import { getAdjudicationResult, getClaimsHistory } from '../services/claimsService'
import { AdjudicateClaimResponse } from '../services/models/adjudication'
import { AddMessageDialog } from './dialogs/AddMessageDialog'
import AdjudicateClaimResultsDialog from './dialogs/adjudicate-claim/AdjudicateClaimResultsDialog'
import { ViewDetailsDialog } from './dialogs/claim-history/ViewDetailsDialog'
import ViewLogDialog from './dialogs/claim-history/ViewLogDialog'
import { RequestsDialog } from './dialogs/RequestsDialog'

interface ClaimHistoryProps {
    claimNumber: string
    data: any
}

const isViewDetailsDisabled = (entry: any) => {
    if (entry.type === '837' || entry.type === '835') {
        return entry.edi === undefined
    } else if (entry.type === 'message') {
        return entry.message === undefined
    }
}

const getClaimNumber = (historyEntry: any, contextClaimNumber: string) => {
    if (isClaimServiceLog(historyEntry.type)) {
        return historyEntry.claimNumber
    }
    switch (historyEntry.type) {
        case '837': {
            if (historyEntry.publicData.claimNumber === contextClaimNumber) {
                return historyEntry.publicData.claimNumber
            }
            return (
                <Link
                    style={{ color: 'blue' }}
                    to={`/claims/${historyEntry.publicData.claimNumber}/summary`}
                >
                    {historyEntry.publicData.claimNumber}
                </Link>
            )
        }
        case 'ADJUDICATE_SUBMIT':
        case 'log':
            return historyEntry.claimNumber
        default: {
            return '--'
        }
    }
}

export const isClaimServiceLog = (type: string) => {
    switch (type?.toUpperCase()) {
        case 'MANUAL_SUBMIT':
        case 'ADJUDICATE_CANCEL':
        case 'PAYMENT_RECEIVED':
        case 'PAYMENT_SENT':
            return true
        default:
            return false
    }
}

export const ClaimHistory = (props: ClaimHistoryProps) => {
    const { data, claimNumber } = props
    const [selectedEntry, setSelectedEntry] = useState<any>({})
    const [requestsDialogOpen, setRequestsDialogOpen] = useState(false)
    const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false)
    const [addMessageDialogOpen, setAddMessageDialogOpen] = useState(false)
    const { identities, lastUpdate, setLastUpdate } = useContext(ApplicationContext)
    const [history, setHistory] = useState<any[]>()
    const [messagingDisabled, setMessagingDisabled] = useState(true)
    const [report, setReport] = useState<{
        reportId?: string
        prevReportId?: string
        reportData?: AdjudicateClaimResponse
    }>()
    const ownOrg = identities.find((entry) => entry.self)?.did

    useEffect(() => {
        if (
            data.claim !== undefined &&
            (data.claim.sender === ownOrg || data.claim.recipient === ownOrg)
        ) {
            setMessagingDisabled(false)
        } else if (
            data.payment !== undefined &&
            (data.payment.sender === ownOrg || data.payment.recipient === ownOrg)
        ) {
            setMessagingDisabled(false)
        }
    })

    const getCurrentlyViewing = (historyEntry: any) => {
        if (historyEntry.publicData?.claimNumber === claimNumber) {
            return (
                <Typography
                    style={{
                        padding: '4px 8px 4px 8px',
                        display: 'inline-block',
                        fontSize: '14px',
                        textAlign: 'center',
                        borderRadius: '5px',
                        backgroundColor: '#C2E0F7',
                    }}
                >
                    Currently Viewing
                </Typography>
            )
        } else {
            return ''
        }
    }

    const getTypeDisplayText = (type: any) => {
        switch (type?.toUpperCase()) {
            case '837':
                return 'Claim Submission (837)'
            case '835':
                return 'Claim Remittance (835)'
            case '275':
                return 'Attachment (275)'
            case '277':
                return 'Acknowledge (277)'
            case '278':
                return 'Prior Auth (278)'
            case 'MESSAGE':
                return 'Message'
            case 'MANUAL_SUBMIT':
                return 'Manual Document Submission'
            case 'ADJUDICATE_SUBMIT':
                return 'Auto Adjudicate: Submit'
            case 'ADJUDICATE_CANCEL':
                return 'Auto Adjudicate: Cancel'
            case 'PAYMENT_SENT':
                return 'Payment: Send to Rhiza'
            case 'PAYMENT_RECEIVED':
                return 'Payment: Received from Rhiza'
            default:
                return '--'
        }
    }

    useEffect(() => {
        const fetchReportData = async (reportId: string) => {
            const { data: _reportData } = await getAdjudicationResult(reportId)
            setReport({ ...report, reportData: _reportData, prevReportId: reportId })
        }
        if (report?.reportId && report.reportId !== report.prevReportId) {
            fetchReportData(report.reportId)
        }
    }, [report])

    const renderSelectedEntryDialog = () => {
        if (selectedEntry?.type === 'ADJUDICATE_SUBMIT') {
            return (
                <AdjudicateClaimResultsDialog
                    resultsId={selectedEntry.data.reportId}
                    open={viewDetailsDialogOpen}
                    onClose={() => setViewDetailsDialogOpen(false)}
                    disableSubmit
                    disableClear
                />
            )
        }

        if (selectedEntry?.type === 'log' || isClaimServiceLog(selectedEntry?.type)) {
            return (
                <ViewLogDialog
                    entry={selectedEntry}
                    open={viewDetailsDialogOpen}
                    close={() => setViewDetailsDialogOpen(false)}
                />
            )
        }

        return (
            <ViewDetailsDialog
                entry={selectedEntry}
                open={viewDetailsDialogOpen}
                close={() => setViewDetailsDialogOpen(false)}
            />
        )
    }

    useEffect(() => {
        getClaimsHistory(claimNumber)
            .then(async (response) => {
                // TODO: better service log handling
                // collision between database type and service log type, had to remap as 'event'
                // maybe remove this? Thought it would be useful to query all logs as 'service-log'
                const dataWithTransformedEvents = response.data.map((entry: any) =>
                    entry.event ? { ...entry, type: entry.event } : entry,
                )
                setHistory(dataWithTransformedEvents)
            })
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .catch((err: Error) => {
                // TO DO
            })
    }, [claimNumber, lastUpdate, data])

    if (history === undefined) {
        return <></>
    }

    return (
        <>
            <Box style={{ borderRadius: '10px', backgroundColor: '#F2F6F9' }}>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography style={{ padding: '30px' }} variant="h6">
                        Claim History
                    </Typography>
                    <Button
                        disabled={messagingDisabled}
                        style={{ marginRight: '30px' }}
                        disableElevation={true}
                        variant="contained"
                        onClick={() => setAddMessageDialogOpen(true)}
                    >
                        Add Message
                    </Button>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Claim Number</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Submitted</TableCell>
                            <TableCell>Sender</TableCell>
                            <TableCell>Recipient</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* For now, skip logs from history table */}
                        {history
                            .filter((e) => e.type !== 'log')
                            .map((entry) => (
                                <TableRow key={entry.sender + entry.submitted}>
                                    <TableCell>{getClaimNumber(entry, claimNumber)}</TableCell>
                                    <TableCell>{getCurrentlyViewing(entry)}</TableCell>
                                    <TableCell>{getTypeDisplayText(entry.type)}</TableCell>
                                    <TableCell>
                                        {new Date(entry.submitted).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        {identities.find(
                                            (identity) => identity.did === entry.sender,
                                        )?.displayName || entry.sender}
                                    </TableCell>
                                    <TableCell>
                                        {identities.find(
                                            (identity) => identity.did === entry.recipient,
                                        )?.displayName || entry.recipient}
                                    </TableCell>
                                    <TableCell width={220}>
                                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                                            <Button
                                                style={{
                                                    borderRadius: '28px',
                                                    minWidth: '100px',
                                                    marginRight: '10px',
                                                }}
                                                size="small"
                                                disableElevation={true}
                                                variant="contained"
                                                onClick={() => {
                                                    setSelectedEntry(entry)
                                                    setRequestsDialogOpen(true)
                                                }}
                                            >
                                                Requests
                                            </Button>
                                            <Button
                                                style={{ borderRadius: '28px', minWidth: '100px' }}
                                                size="small"
                                                disableElevation={true}
                                                disabled={isViewDetailsDisabled(entry)}
                                                variant="contained"
                                                onClick={() => {
                                                    logEvent('history_details_click', {
                                                        appName: window.location.host,
                                                    })
                                                    setSelectedEntry(entry)
                                                    setViewDetailsDialogOpen(true)
                                                }}
                                            >
                                                Details
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </Box>
            <RequestsDialog
                entry={selectedEntry}
                open={requestsDialogOpen}
                close={() => setRequestsDialogOpen(false)}
            />
            {renderSelectedEntryDialog()}

            <AddMessageDialog
                setLastUpdate={setLastUpdate}
                claimNumber={claimNumber}
                open={addMessageDialogOpen}
                close={() => setAddMessageDialogOpen(false)}
            />
        </>
    )
}
